import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Animated from 'react-native-reanimated';
import { Keyboard, StyleSheet, View } from 'react-native';
import { AppView, CardView, rem, SCROLL_INDICATOR_INSETS, SettingsItemInput, withAnimatedNavigationHeader, } from 'design-system';
import { IconCameraTransfer } from 'design-system/icons';
import { KeyboardAvoidingButton } from 'design-system/KeyboardAvoidingButton';
import Modal from 'utils/packages/Modal';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import Text from 'design-system/Text';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { InfoModal } from 'features/invest/components/InfoModal';
import { AccountTypeDisplayLong, transferProductString } from 'features/invest/constants';
import { useAutoFocusInput } from 'hooks/useAutoFocusInput';
import useColors from 'hooks/useColors';
import { formatAmountInput } from 'utils/formatting';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import Amplitude from 'utils/amplitude';
import { amplitudeSource } from '../utils/analytics';
import { isWeb } from '../../../constants';
const MAX_BALANCE_AMOUNT = 10_000_000_000;
const strings = {
    title: (t, type) => t === 'JISA'
        ? `Enter your total ${AccountTypeDisplayLong[t]} balance`
        : `Enter your total ${transferProductString[type]} ${t} balance`,
    subTitle: (t, type) => t === 'JISA'
        ? `Enter the estimated total value of your ${AccountTypeDisplayLong[t]}. If your estimate differs from the actual amount by more than 10%, your transfer may be rejected`
        : `Enter the estimated total value of your ${transferProductString[type]} ${t}. If your estimate differs from the actual amount by more than 10%, your transfer may be rejected`,
    totalBalance: (t, type) => t === 'JISA' ? `${AccountTypeDisplayLong[t]} total balance` : `${transferProductString[type]} ${t} total balance`,
    enter: 'Enter amount...',
    partialTransferNotSupported: 'Partial transfers are not supported. ', // space intentional
    learnMore: 'Learn more',
    partialTransfer: (t) => `Partial ${t === 'JISA' ? AccountTypeDisplayLong[t] : t} transfer`,
    partialTransferDescription: (t) => [
        `Sorry, we’re unable to offer partial ${t === 'JISA' ? AccountTypeDisplayLong[t] : t} transfers.`,
        'If your estimate differs from the actual amount by more than 10%, your transfer may be rejected.',
    ],
    continue: 'Continue',
};
const ProductTransferBalanceScreen = withAnimatedNavigationHeader(({ navigation, route, scrollHandler }) => {
    const colors = useColors();
    const { type, provider, accountId, ...identification } = route.params;
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const accountType = accountId ? 'JISA' : 'ISA';
    useEffect(() => {
        Amplitude.logEvent('Transfer Balance Screen', {
            source: amplitudeSource,
            account: accountId ? 'jisa' : 'isa',
        });
    }, [accountId]);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton />,
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    const inputRef = useRef(null);
    const [inputValue, setInputValue] = useState({
        formattedValue: '',
        rawValue: 0,
    });
    const isValid = inputValue.rawValue > 0;
    const handleContinue = useCallback(() => {
        Keyboard.dismiss();
        Modal.hide();
        const balance = inputValue.rawValue;
        navigation.navigate('ProductTransferTaxYear', {
            type,
            provider,
            balance,
            accountId,
            ...identification,
        });
    }, [inputValue.rawValue, type, provider, accountId, identification]);
    const onChangeText = useCallback((text) => {
        const { value, rawAmount } = formatAmountInput(text, 'GBP');
        const numberRawAmount = Number(rawAmount);
        if (numberRawAmount < MAX_BALANCE_AMOUNT) {
            setInputValue({ formattedValue: value, rawValue: numberRawAmount });
        }
    }, []);
    const showLearnMore = useCallback(() => {
        Keyboard.dismiss();
        InfoModal.show(strings.partialTransfer(accountType), <View style={styles.partialTransferModalContent}>
          <Text TextThin-14 Secondary centered>
            {strings.partialTransferDescription(accountType)[0]}
          </Text>
          <Text TextThin-14 Secondary centered>
            {strings.partialTransferDescription(accountType)[1]}
          </Text>
        </View>);
    }, [accountType]);
    useAutoFocusInput(inputRef);
    return (<AppView withFrame={false}>
        <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} keyboardShouldPersistTaps="handled" contentContainerStyle={paddingHorizontalStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
          <ScreenInfoHeader title={strings.title(accountType, type)} subTitle={strings.subTitle(accountType, type)}/>
          <CardView noMarginTop noMarginBottom>
            <SettingsItemInput ref={inputRef} keyboardType="decimal-pad" onChangeText={onChangeText} placeholder={strings.enter} value={inputValue.formattedValue} underlineColorAndroid="transparent" selectionColor={colors.text.selection} description={strings.totalBalance(accountType, type)} icon={<IconCameraTransfer gradientKey="lightPink"/>}/>
          </CardView>

          <View style={styles.inputInfo}>
            <Text TextThin-14 Secondary>
              {strings.partialTransferNotSupported}
              <Text Text-14 Primary onPress={showLearnMore} suppressHighlighting>
                {strings.learnMore}
              </Text>
            </Text>
          </View>
        </Animated.ScrollView>
        <KeyboardAvoidingButton brand disabled={!isValid} title={strings.continue} onPress={handleContinue}/>
      </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styles = StyleSheet.create({
    inputInfo: {
        margin: rem(16),
    },
    partialTransferModalContent: {
        gap: rem(10),
    },
});
export default ProductTransferBalanceScreen;
